import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance, validateAuthConfig } from './authConfig';
import ThemeProvider from './theme/ThemeProvider';
import { AuthProvider } from './contexts/AuthContext';
import { GradingProvider } from './contexts/GradingContext';
import { SocketProvider } from './contexts/SocketContext';
import BreadNav from './components/BreadNav';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute'; 
import HomePage from './pages/HomePage';
import AssessmentPage from './pages/AssessmentPage';
import MeetingPage from './pages/CallPage'
import FeedbackPage from './pages/FeedbackPage';
import FeedbackReportPage from './pages/FeedbackReportPage';
import { NavigationBlockProvider } from './contexts/NavigationBlockContext';
import AdminPage from './pages/AdminPage';
import AdminMasterAssignmentsPage from './pages/AdminMasterAssignmentsPage';
import AssessmentTabs from './pages/AssessmentTabs';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './services/telemetry';
import AdminPromptStructures from './pages/AdminPromptStructures';
import AdminPromptStructureFormPage from './pages/AdminPromptStructureFormPage';
import QuickAssign from './pages/QuickAssign';

function AppContent() {
  return (
    <ProtectedRoute>
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default'
      }}>
        <BreadNav />
        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <Routes>
            {/* Public/General Protected Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/assessment" element={<AssessmentPage />} />
            <Route path="/call" element={<MeetingPage />} />
            <Route path="/call/:assessmentId" element={<MeetingPage />} />
            <Route path="/assign/:assessmentId" element={<QuickAssign />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/feedback/:feedbackId" element={<FeedbackReportPage />} />

            {/* Admin-only Protected Routes */}
            <Route element={<AdminRoute />}>
              <Route path='/admin' element={<AdminPage />} />
              <Route path='/admin/assignments' element={<AdminMasterAssignmentsPage />} />
              <Route path="/admin/assessments/create" element={<AssessmentTabs mode="create" />} />
              <Route path="/admin/assessments/:scenario_id" element={<AssessmentTabs mode="edit" />} />  
              <Route path="/admin/prompt-structures" element={<AdminPromptStructures />} />
              <Route path="/admin/prompt-structures/create" element={<AdminPromptStructureFormPage />} />
              <Route path="/admin/prompt-structures/:id" element={<AdminPromptStructureFormPage />} />
            </Route>
          </Routes>
        </Box>
      </Box>
    </ProtectedRoute>
  );
}


function App() {
  // Validate auth configuration on app startup
  validateAuthConfig();

  const [msalInitialized, setMsalInitialized] = React.useState(false);

  useEffect(() => {
    // Initialize MSAL instance
    msalInstance.initialize().then(() => {
      setMsalInitialized(true);

      // Handle redirect callback on app startup
      msalInstance.handleRedirectPromise().then((response) => {
        if (response) {
          msalInstance.setActiveAccount(response.account);
        }
      }).catch((error) => {
        console.error('Redirect handling failed:', error);
      });
    }).catch((error) => {
      console.error('MSAL initialization failed:', error);
    });
  }, []);

  if (!msalInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            minWidth: 300
          }}
        >
          <CircularProgress size={50} />
          <Typography variant="h6" align="center">
            Initializing AI Coaching Platform...
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Setting up secure authentication and connections
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <AppInsightsContext.Provider value={reactPlugin}>
          <ThemeProvider>
            <SocketProvider>
              <GradingProvider>
                <NavigationBlockProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </NavigationBlockProvider>
              </GradingProvider>
            </SocketProvider>
          </ThemeProvider>
        </AppInsightsContext.Provider> 
      </AuthProvider>
    </MsalProvider>
  );
}

export default App;
